import { useSyncExternalStore } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { ApiError } from "../../api/client";
import { isRelease150 } from "../../lib/release150";
import {
  DEFAULT_AI_OFF_REASON,
  VIPER_QK,
  isViperUnavailableError,
  viperApi,
  type ViperAnswerResponse,
  type ViperPersona
} from "../../api/viperApi";

// Shared conversation store for the floating VIPER launcher panel and the full /viper page.
// Holds the active server session id, selected persona ("hat"), turns in the active session,
// and any "Ask VIPER about this" launcher prefill request.

export interface ViperStoreState {
  sessionId: string | null;
  persona: ViperPersona;
  turns: ViperAnswerResponse[];
  selectedTurnIndex: number | null;
  asking: boolean;
  pendingQuestion: string | null;
  unavailableReason: string | null;
  errorMessage: string | null;
}

const initialState: ViperStoreState = {
  sessionId: null,
  persona: "planner",
  turns: [],
  selectedTurnIndex: null,
  asking: false,
  pendingQuestion: null,
  unavailableReason: null,
  errorMessage: null
};

let state: ViperStoreState = { ...initialState };
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((listener) => listener());
}

function setState(patch: Partial<ViperStoreState>) {
  state = { ...state, ...patch };
  emit();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getViperStoreState(): ViperStoreState {
  return state;
}

export function useViperStore(): ViperStoreState {
  return useSyncExternalStore(subscribe, getViperStoreState, getViperStoreState);
}

export function setViperPersona(persona: ViperPersona) {
  setState({ persona });
}

export function setViperSelectedTurnIndex(index: number | null) {
  setState({ selectedTurnIndex: index });
}

export function setViperSession(sessionId: string | null, turns?: ViperAnswerResponse[], persona?: ViperPersona) {
  const nextTurns = turns ?? state.turns;
  setState({
    sessionId,
    turns: nextTurns,
    ...(persona ? { persona } : {}),
    selectedTurnIndex: nextTurns.length > 0 ? nextTurns.length - 1 : null,
    errorMessage: null
  });
}

export function recordViperTurn(answer: ViperAnswerResponse) {
  const nextTurns = [...state.turns, answer];
  setState({
    sessionId: answer.sessionId || state.sessionId,
    turns: nextTurns,
    selectedTurnIndex: nextTurns.length - 1,
    asking: false,
    pendingQuestion: null,
    errorMessage: null
  });
}

export async function askViperQuestion(
  question: string,
  queryClient: QueryClient,
  overridePersona?: ViperPersona
): Promise<ViperAnswerResponse | null> {
  const trimmed = question.trim();
  if (!trimmed || state.asking) return null;

  const activePersona = overridePersona ?? state.persona;
  setState({
    asking: true,
    pendingQuestion: trimmed,
    errorMessage: null
  });

  try {
    const answer = await viperApi.ask({
      question: trimmed,
      sessionId: state.sessionId,
      persona: activePersona,
      // Release 1.5.0 (O-R61): the next questions are chosen by rule, so the model is not asked to write them.
      followups: !isRelease150()
    });
    recordViperTurn(answer);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [...VIPER_QK.sessions] }),
      queryClient.invalidateQueries({ queryKey: [...VIPER_QK.session(answer.sessionId)] })
    ]);
    return answer;
  } catch (error) {
    if (isViperUnavailableError(error)) {
      const apiErr = error instanceof ApiError ? error : null;
      const reason =
        apiErr && apiErr.status === 503 && apiErr.message
          ? plainTextExplanation(apiErr.message)
          : DEFAULT_AI_OFF_REASON;
      setState({
        asking: false,
        pendingQuestion: null,
        unavailableReason: reason,
        errorMessage: null
      });
      return null;
    }

    const message =
      error instanceof ApiError
        ? plainTextExplanation(error.message || error.title)
        : "VIPER could not answer that question right now. Try again in a moment.";
    setState({
      asking: false,
      pendingQuestion: null,
      errorMessage: message
    });
    return null;
  }
}

export function startNewViperConversation() {
  setState({
    sessionId: null,
    turns: [],
    selectedTurnIndex: null,
    asking: false,
    pendingQuestion: null,
    errorMessage: null
  });
}

export async function clearViperConversation(queryClient?: QueryClient): Promise<void> {
  const currentId = state.sessionId;
  startNewViperConversation();
  if (currentId) {
    try {
      await viperApi.deleteSession(currentId);
    } catch {
      // Starting a new local conversation still succeeds if the session was already removed.
    }
  }
  if (queryClient) {
    await queryClient.invalidateQueries({ queryKey: [...VIPER_QK.sessions] });
  }
}

// ── Launcher prefill requests ────────────────────────────────────────────────────────────────────────────────────

export interface ViperLauncherRequest {
  id: number;
  prefill: string;
}

let launcherRequest: ViperLauncherRequest | null = null;
const launcherListeners = new Set<() => void>();

function subscribeLauncher(listener: () => void) {
  launcherListeners.add(listener);
  return () => launcherListeners.delete(listener);
}

export function openViperLauncher(prefill = "") {
  launcherRequest = { id: (launcherRequest?.id ?? 0) + 1, prefill };
  launcherListeners.forEach((listener) => listener());
}

export function useViperLauncherRequest(): ViperLauncherRequest | null {
  return useSyncExternalStore(subscribeLauncher, () => launcherRequest, () => launcherRequest);
}

export function resetViperStore() {
  state = { ...initialState };
  listeners.clear();
  launcherRequest = null;
  launcherListeners.clear();
}

/**
 * Strips internal decision/question IDs, rule codes, settings keys, and C# class/table names from server strings
 * so every message rendered on screen stays in plain shop-floor and office words.
 */
export function plainTextExplanation(raw: string | null | undefined): string {
  if (!raw) return "";
  return raw
    .replace(/\s*\((?:[QD]-O?\d+|O-R\d+)[^)]*\)/g, "")
    .replace(/\b(?:[QD]-O?\d+|O-R\d+)\b/g, "")
    .replace(/\bIAiClient\b/g, "the AI service")
    .replace(/\b(?:app|ops|plan|kb|stg)\.[a-z0-9_]+\b/gi, "the records")
    .replace(/\b(?:ai|viper|ops|kb)\.[a-z0-9_.]+\b/gi, "the governed setting")
    .replace(/\s{2,}/g, " ")
    .trim();
}

